import { useState } from 'react';
import z from 'zod';

import { isFinlayStaffEmail } from '@/modules/auth/utils/isFinlayStaff';
import {
  companyClientStatusOptions,
  isCompanyClientStatus,
} from '@/modules/companies/clientStatus';
import type { CompanyApi, CompanyRequestApi } from '@/modules/companies/types';
import { Form } from '@/modules/forms/components/Form';
import { FormInput } from '@/modules/forms/components/FormInput';
import { FormMultiSelect } from '@/modules/forms/components/FormMultiSelect';
import { FormRadioGroup } from '@/modules/forms/components/FormRadioGroup';
import { FormSelect } from '@/modules/forms/components/FormSelect';
import { FormTextarea } from '@/modules/forms/components/FormTextarea';
import { Button } from '@/modules/ui/components/button/Button';
import { TagInput } from '@/modules/ui/components/tag-input/TagInput';
import { useUsersQuery } from '@/modules/users/queries/useUsersQuery';

const booleanSchema = z
  .enum(['true', 'false'])
  .transform((value) => value === 'true');

const companyFormSchema = z.object({
  name: z.string().min(1, 'Company name is required'),
  description: z.string().min(1, 'Company description is required'),
  clientStatus: z
    .string()
    .refine(isCompanyClientStatus, 'Client status is required'),
  skipOnboarding: booleanSchema,
  websiteUrl: z.string().optional(),
  location: z.string().optional(),
  ownerIds: z.array(z.string()).min(1, 'At least one owner is required'),
});

type CompanyFormValues = z.infer<typeof companyFormSchema>;

interface CompanyFormProps {
  initialValues?: CompanyApi;
  submitText?: string;
  isPending?: boolean;
  onSubmit: (values: CompanyRequestApi) => void;
  onCancel?: () => void;
}

export const CompanyForm = ({
  initialValues,
  submitText,
  isPending,
  onSubmit,
  onCancel,
}: CompanyFormProps) => {
  const [hiringStages, setHiringStages] = useState<string[]>(
    initialValues?.hiringStages || [],
  );

  const { data: users } = useUsersQuery();

  const ownerOptions = (users ?? [])
    .filter((u) => isFinlayStaffEmail(u.email))
    .map((u) => ({
      value: u.id,
      label:
        u.firstName && u.lastName
          ? `${u.firstName} ${u.lastName}`
          : (u.email ?? u.id),
    }));

  const handleFormSubmit = (values: CompanyFormValues) => {
    onSubmit({
      ...values,
      hiringStages: hiringStages.length > 0 ? hiringStages : undefined,
      skipOnboarding: values.skipOnboarding,
    });
  };

  const defaultFormValues = {
    name: initialValues?.name || '',
    description: initialValues?.description || '',
    clientStatus: initialValues?.clientStatus ?? '',
    skipOnboarding: String(initialValues?.onboarding?.skipped ?? false) as
      | 'true'
      | 'false',
    websiteUrl: initialValues?.websiteUrl || '',
    location: initialValues?.location || '',
    ownerIds: initialValues?.owners?.map((o) => o.id) ?? [],
  };

  return (
    <Form
      defaultValues={defaultFormValues}
      schema={companyFormSchema}
      onSubmit={handleFormSubmit}
    >
      <div className="space-y-8">
        <FormInput
          name="name"
          label="Name"
          placeholder="Company name"
          required
        />

        <FormTextarea
          name="description"
          label="Description"
          placeholder="Company description..."
          rows={4}
          required
        />

        <FormSelect
          name="clientStatus"
          label="Client status"
          placeholder="Select client status..."
          options={companyClientStatusOptions}
          required
        />

        <FormMultiSelect
          name="ownerIds"
          label="Owners"
          placeholder="Select owners..."
          options={ownerOptions}
          required
        />

        <FormRadioGroup
          required
          name="skipOnboarding"
          label="Onboarding"
          options={[
            { value: 'false', label: 'Onboarding enabled' },
            {
              value: 'true',
              label: 'Skip onboarding for new users in this company',
            },
          ]}
        />

        <FormInput
          name="websiteUrl"
          label="Website"
          type="url"
          placeholder="https://www.company.com"
        />

        <FormInput
          name="location"
          label="Headquarters"
          placeholder="San Francisco, CA"
        />

        <TagInput
          label="Hiring Stages"
          placeholder="Add hiring stages (press Enter or comma to add)"
          value={hiringStages}
          onChange={setHiringStages}
        />

        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button type="submit" loading={isPending}>
            {submitText || (initialValues ? 'Save' : 'Add')}
          </Button>
        </div>
      </div>
    </Form>
  );
};
